/**
 * This store is used to hold all of the permissions that the currently authed user has.
 * When a user is authenticated their active permissions should be added into this store,
 * and then this store should be queried to see whether a user has a required permission.
 */
interface Permission {
  category: string;
  description: string;
  id: string;
  name: string;
}

interface PermissionsState {
  activePermissions: Permission[];
  activeFeatures: string[];
  splitClient: any,
}

const getInitialState = (): PermissionsState => ({
  activePermissions: [],
  activeFeatures: [],
  splitClient: null,
});

export const getters = {
  activePermissionNames(state: PermissionsState) {
    return state.activePermissions.map((permission) => permission.name);
  },
  hasFeatures: (state) => (features: string[]): boolean => features.every(
    (feature) => state.activeFeatures.includes(feature),
  ),
};

export const mutations = {
  set(state: PermissionsState, { permissions }) {
    state.activePermissions = permissions;
  },
  resetState(state) {
    Object.assign(state, getInitialState());
  },
};

export const actions = {
  async initFeatureFlags({ state }, { userId }) {
    if (state.splitClient) return;
    if (!userId) {
      console.error('userId is required');
      return;
    }
    const { SplitFactory } = await import('@splitsoftware/splitio');
    const factory = SplitFactory({
      core: {
        authorizationKey: process.env.VUE_APP_SPLIT_API_KEY as string,
        key: userId,
      },
    });
    state.splitClient = factory.client();
  },
  /**
   * This should be called on app startup, requesting all the features that will be required in the
   * app. This prevents having to wait for them when we need them.
   * Permissions can then be tested using a computed prop. eg:
   * hasSsasPermission(): boolean {
      return this.$store.getters['sPermissions/hasFeatures'](['ssas']);
    },
   */
  async updateFeatureFlags({ dispatch, state }, { features, userDetails }) {
    await dispatch('initFeatureFlags', { userId: userDetails.userId });

    const client = state.splitClient as SplitIO.IClient;

    return new Promise((resolve) => {
      client.on(client.Event.SDK_READY, () => {
        // getTreatments takes a list of features we want to check and an object full of data about
        // the user. This data tells split the user name, user ID, customer name etc. It can
        // actually be dynamic data (ie. hasAcceptedTerms or timeSinceLogin) but for now
        // we just pass this static user data
        // results will have the following form:
        // {
        //   SPLIT_NAME_1: 'on',
        //   SPLIT_NAME_2: 'green'
        // }
        // For now we're only using 'on' and 'off'
        const results = client.getTreatments(features, userDetails);
        state.activeFeatures = Object.keys(results).map((key) => (results[key] === 'on') && key);
        resolve();
      });
    });
  },
};

export default {
  mutations,
  actions,
  getters,
  namespaced: true,
  state: getInitialState,
};
